import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from './Icon';

/** Rows per page on the admin list pages. */
export const PAGE_SIZE = 20;
/** Cards per page on the card-grid pages (Legal Services, Service Categories). */
export const CARD_PAGE_SIZE = 12;

interface PaginationProps {
  /** 1-based. */
  page: number;
  pageSize: number;
  /** Rows across every page. */
  total: number;
  onChange: (page: number) => void;
  disabled?: boolean;
}

/** 1 … 4 5 [6] 7 8 … 20 — first, last and the pages around the current one. */
const pageList = (page: number, totalPages: number): (number | 'gap')[] => {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
  const keep = new Set([1, totalPages, page - 1, page, page + 1]);
  if (page <= 3) [2, 3, 4].forEach((p) => keep.add(p));
  if (page >= totalPages - 2) [totalPages - 3, totalPages - 2, totalPages - 1].forEach((p) => keep.add(p));
  const pages = Array.from(keep)
    .filter((p) => p >= 1 && p <= totalPages)
    .sort((a, b) => a - b);
  const out: (number | 'gap')[] = [];
  pages.forEach((p, i) => {
    if (i > 0 && p - pages[i - 1] > 1) out.push('gap');
    out.push(p);
  });
  return out;
};

const disabledStyle: React.CSSProperties = { opacity: 0.45, cursor: 'not-allowed' };

/**
 * "Showing 21–40 of 87" with page numbers and Previous / Next. Renders
 * nothing when everything fits on one page. Put it straight after the table
 * it pages: changing page scrolls that table back into view.
 */
const Pagination: React.FC<PaginationProps> = ({ page, pageSize, total, onChange, disabled }) => {
  const navRef = useRef<HTMLElement | null>(null);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (totalPages <= 1) return null;

  const go = (next: number) => {
    if (next < 1 || next > totalPages || next === page) return;
    onChange(next);
    const list = navRef.current?.previousElementSibling as HTMLElement | null;
    list?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const prevOff = disabled || page <= 1;
  const nextOff = disabled || page >= totalPages;

  return (
    <nav ref={navRef} className="flex justify-between items-center mt-5 flex-wrap gap-3" aria-label="Pagination">
      <div className="text-sm text-g500">
        Showing <strong className="text-ink">{from}</strong>–<strong className="text-ink">{to}</strong> of{' '}
        <strong className="text-ink">{total}</strong>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => go(page - 1)}
          disabled={prevOff}
          style={prevOff ? disabledStyle : undefined}
        >
          <Icon name="arrow-left" size={14} /> Previous
        </button>
        {pageList(page, totalPages).map((p, i) =>
          p === 'gap' ? (
            <span key={`gap-${i}`} className="px-1 text-sm text-g400">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              className={'btn btn-sm ' + (p === page ? 'btn-primary' : 'btn-secondary')}
              style={{ minWidth: 34, justifyContent: 'center', ...(disabled && p !== page ? disabledStyle : {}) }}
              onClick={() => go(p)}
              disabled={disabled}
              aria-current={p === page ? 'page' : undefined}
              aria-label={`Page ${p}`}
            >
              {p}
            </button>
          )
        )}
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => go(page + 1)}
          disabled={nextOff}
          style={nextOff ? disabledStyle : undefined}
        >
          Next <Icon name="chevron-right" size={14} />
        </button>
      </div>
    </nav>
  );
};

/**
 * Pages a list that's already fully loaded. Goes back to page 1 whenever
 * `resetKey` changes (filters, search) and stays in range when rows are removed.
 */
export const usePagedRows = <T,>(rows: T[], pageSize: number = PAGE_SIZE, resetKey?: unknown) => {
  const [page, setPage] = useState<number>(1);
  useEffect(() => {
    setPage(1);
  }, [resetKey]);
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const current = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => rows.slice((current - 1) * pageSize, current * pageSize),
    [rows, current, pageSize]
  );
  return { page: current, setPage, pageRows, total: rows.length };
};

export default Pagination;
